import { QueryDocumentSnapshot, QuerySnapshot } from 'firebase/firestore'
import { z } from 'zod'

const baseConciergeSchema = z.object({
  id: z.string(),
  wisdomId: z.string(),
  email: z.string(),
  enabled: z.boolean().default(true),
  useIframePreview: z.boolean().optional(),
  hideHalo: z.boolean().optional(),
  initialPopupMessages: z.array(z.string()).optional(),
  faq: z.string().optional(),
  is_live: z.boolean().optional(),
})

const webConciergeSchema = baseConciergeSchema.extend({
  type: z.literal('web').default('web'),
  url: z.string(),
  avatarImage: z.string(),
  avatarWidth: z.number().default(90),
  avatarHeight: z.number().default(90),
  buttonText: z.string(),
  textColor: z.string().default('white'),
  backgroundColor: z.string(),
})

const contentConciergeSchema = baseConciergeSchema.extend({
  type: z.literal('content'),
  name: z.string().default('content-default'),
})

const rawConciergeSchema = z.discriminatedUnion('type', [
  webConciergeSchema,
  contentConciergeSchema,
])
type RawConcierge = z.infer<typeof rawConciergeSchema>

export const conciergeSchema = rawConciergeSchema.transform(addSlug)
export type Concierge = z.infer<typeof conciergeSchema>
function addSlug(concierge: RawConcierge) {
  const slug =
    concierge.type === 'web' ? new URL(concierge.url).hostname : concierge.name
  return { ...concierge, slug }
}

export function isWebConcierge(concierge: Concierge) {
  return concierge.type === 'web' || !concierge.type
}

export function isContentConcierge(concierge: Concierge) {
  return concierge.type === 'content'
}

export function parseConcierge(
  doc: QueryDocumentSnapshot<Partial<Omit<Concierge, 'id'>>>,
): Concierge {
  const data = doc.data()

  const nullStripped = Object.fromEntries(
    Object.entries(data).map(([k, v]) => [k, v === null ? undefined : v]),
  ) as Record<string, unknown>

  const backfilledWebType = {
    id: doc.id,
    type: (nullStripped.type as string) ?? 'web',
    ...nullStripped,
  }

  return conciergeSchema.parse(backfilledWebType)
}

export function parseConcierges(snapshot: QuerySnapshot): Concierge[] {
  const concierges: Concierge[] = []
  snapshot.docs.forEach((doc) => {
    try {
      concierges.push(parseConcierge(doc))
    } catch (error) {
      console.error('Failed to parse concierge', {
        id: doc.id,
        data: doc.data(),
        error,
      })
    }
  })
  return concierges
}

export const schemas = {
  base: baseConciergeSchema,
  web: webConciergeSchema,
  content: contentConciergeSchema,
  legacy: conciergeSchema,
}
